/**
 * Side-panel context rail — id snippet, date, amount with sign coloring,
 * description, source pill, close button, stacked vertically. The same data
 * (amount leads, then description, source, date, id) reads top-to-bottom so the
 * eye lands on the action column to its right in the two-column classification
 * panel. Mono/sign-color identity throughout; close button sits at the rail's
 * top-right. Entity-specific extras render via the `children` slot.
 */
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatAmount, formatDate } from '@/components/finance/utils';

interface PanelHeaderProps {
  id: string;
  date: string;
  amount: number;
  description: string;
  sourceLabel: string;
  onClose: () => void;
  /** Optional entity-specific slot rendered alongside the source pill (e.g. a reference number). */
  children?: ReactNode;
}

function SourceChip({ sourceLabel }: { sourceLabel: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-700">
      <span className="w-[22px] h-[22px] rounded-md grid place-items-center bg-slate-500 text-white text-[9.5px] font-extrabold font-mono tracking-[-.02em]">
        {sourceLabel.slice(0, 2).toUpperCase()}
      </span>
      <span className="font-semibold">{sourceLabel}</span>
    </span>
  );
}

export function PanelHeader({
  id,
  date,
  amount,
  description,
  sourceLabel,
  onClose,
  children,
}: PanelHeaderProps) {
  const isPositive = amount >= 0;

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div
          className={cn(
            'text-[26px] font-extrabold tracking-[-.02em] font-mono leading-none break-all',
            isPositive ? 'text-emerald-700' : 'text-rose-700',
          )}
        >
          {formatAmount(amount)}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1 hover:bg-slate-100 text-slate-500"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="text-xs text-slate-700 break-all font-mono">{description}</div>
      <SourceChip sourceLabel={sourceLabel} />
      <div className="text-xs text-slate-500 font-medium">{formatDate(date)}</div>
      <div className="text-xs text-slate-400 font-mono break-all">{id.slice(0, 8)}</div>
      {children}
    </div>
  );
}
